import React, { useEffect, useRef, useState } from "react";
import "./AudioRecorder.scss";

type SavedRecording = {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  durationText: string;
  createdAt: string;
  base64Audio: string;
};

const STORAGE_KEY = "local_audio_recordings";

function formatTime(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0"
  )}`;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      resolve(reader.result as string);
    };

    reader.onerror = () => {
      reject(new Error("Failed to convert audio to base64"));
    };

    reader.readAsDataURL(blob);
  });
}

function getStoredRecordings(): SavedRecording[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) return [];

    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) return [];

    return parsed;
  } catch (error) {
    console.error("Failed to read local recordings:", error);
    return [];
  }
}

function saveRecordingsToStorage(recordings: SavedRecording[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(recordings));
  } catch (error) {
    console.error("Failed to save recording to localStorage:", error);
    throw new Error(
      "Recording is too large for localStorage. Use IndexedDB for larger audio files."
    );
  }
}

const AudioRecorder: React.FC = () => {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [status, setStatus] = useState("Ready to record");
  const [recordings, setRecordings] = useState<SavedRecording[]>([]);
  const [activePlayingId, setActivePlayingId] = useState<string | null>(null);

  useEffect(() => {
    setRecordings(getStoredRecordings());

    return () => {
      stopTimer();

      mediaStreamRef.current?.getTracks().forEach((track) => {
        track.stop();
      });
    };
  }, []);

  const startTimer = () => {
    stopTimer();

    timerRef.current = window.setInterval(() => {
      setRecordingSeconds((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus("Audio recording is not supported in this browser");
        return;
      }

      chunksRef.current = [];
      setRecordingSeconds(0);
      setStatus("Requesting microphone permission...");

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      mediaStreamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";

      const recorder = new MediaRecorder(stream, {
        mimeType,
      });

      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstart = () => {
        setIsRecording(true);
        setStatus("Recording...");
        startTimer();
      };

      recorder.onerror = () => {
        setStatus("Something went wrong while recording");
        setIsRecording(false);
        stopTimer();
      };

      recorder.onstop = async () => {
        stopTimer();

        mediaStreamRef.current?.getTracks().forEach((track) => {
          track.stop();
        });

        mediaStreamRef.current = null;

        const audioBlob = new Blob(chunksRef.current, {
          type: recorder.mimeType || "audio/webm",
        });

        if (!audioBlob.size) {
          setStatus("No audio was recorded");
          setIsRecording(false);
          return;
        }

        try {
          const base64Audio = await blobToBase64(audioBlob);

          const createdAt = new Date();

          const newRecording: SavedRecording = {
            id: crypto.randomUUID(),
            name: `Recording ${createdAt.toLocaleString()}`,
            mimeType: audioBlob.type,
            size: audioBlob.size,
            durationText: formatTime(recordingSeconds),
            createdAt: createdAt.toISOString(),
            base64Audio,
          };

          const updatedRecordings = [newRecording, ...recordings];

          saveRecordingsToStorage(updatedRecordings);

          setRecordings(updatedRecordings);
          setStatus("Recording saved locally");
        } catch (error: any) {
          console.error(error);
          setStatus(error?.message || "Failed to save recording locally");
        }

        setIsRecording(false);
      };

      recorder.start();
    } catch (error) {
      console.error(error);
      setStatus("Microphone permission denied or unavailable");
      setIsRecording(false);
      stopTimer();
    }
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;

    if (!recorder) return;

    if (recorder.state !== "inactive") {
      recorder.stop();
      setStatus("Saving recording...");
    }
  };

  const deleteRecording = (id: string) => {
    const updatedRecordings = recordings.filter((item) => item.id !== id);

    saveRecordingsToStorage(updatedRecordings);
    setRecordings(updatedRecordings);

    if (activePlayingId === id) {
      setActivePlayingId(null);
    }

    setStatus("Recording deleted");
  };

  const clearAllRecordings = () => {
    localStorage.removeItem(STORAGE_KEY);
    setRecordings([]);
    setActivePlayingId(null);
    setStatus("All recordings cleared");
  };

  const getFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="audio-recorder">
      <div className="audio-recorder__card">
        <div className="audio-recorder__top">
          <div>
            <p className="audio-recorder__eyebrow">Local recorder</p>
            <h2>Audio Recorder</h2>
            <p className="audio-recorder__subtitle">
              Record audio, save it in browser localStorage, and play it back.
            </p>
          </div>

          <div
            className={`audio-recorder__pulse ${
              isRecording ? "audio-recorder__pulse--active" : ""
            }`}
          >
            <span />
          </div>
        </div>

        <div className="audio-recorder__timer">
          <span>{formatTime(recordingSeconds)}</span>
          <small>{status}</small>
        </div>

        <div className="audio-recorder__actions">
          {!isRecording ? (
            <button
              type="button"
              className="audio-recorder__btn audio-recorder__btn--record"
              onClick={startRecording}
            >
              Start Recording
            </button>
          ) : (
            <button
              type="button"
              className="audio-recorder__btn audio-recorder__btn--stop"
              onClick={stopRecording}
            >
              Stop Recording
            </button>
          )}

          <button
            type="button"
            className="audio-recorder__btn audio-recorder__btn--ghost"
            onClick={clearAllRecordings}
            disabled={!recordings.length || isRecording}
          >
            Clear All
          </button>
        </div>

        <div className="audio-recorder__listHeader">
          <h3>Saved recordings</h3>
          <span>{recordings.length} saved</span>
        </div>

        {recordings.length === 0 ? (
          <div className="audio-recorder__empty">
            <strong>No recordings yet</strong>
            <p>Click “Start Recording” and allow microphone access.</p>
          </div>
        ) : (
          <div className="audio-recorder__list">
            {recordings.map((recording) => (
              <div className="audio-recorder__item" key={recording.id}>
                <div className="audio-recorder__itemInfo">
                  <strong>{recording.name}</strong>

                  <div className="audio-recorder__meta">
                    <span>{recording.durationText}</span>
                    <span>{getFileSize(recording.size)}</span>
                    <span>
                      {new Date(recording.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                <audio
                  className="audio-recorder__player"
                  controls
                  src={recording.base64Audio}
                  onPlay={() => setActivePlayingId(recording.id)}
                  onPause={() => setActivePlayingId(null)}
                  onEnded={() => setActivePlayingId(null)}
                />

                <div className="audio-recorder__itemActions">
                  <a
                    className="audio-recorder__download"
                    href={recording.base64Audio}
                    download={`${recording.name}.webm`}
                  >
                    Download
                  </a>

                  <button
                    type="button"
                    className="audio-recorder__delete"
                    onClick={() => deleteRecording(recording.id)}
                    disabled={activePlayingId === recording.id}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioRecorder;